import { useState } from "react";
import { Trash2, Users, X } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { IDS } from "../constants/testIds";
import PTTButton from "./PTTButton";
import { Waveform } from "./Waveform";

export default function TalkgroupCard({ tg, contacts, status, onPTTStart, onPTTEnd, onDelete, onDropContact, onRemoveMember }) {
  const [over, setOver] = useState(false);
  const members = tg.member_ids.map(id => contacts.find(c => c.id === id)).filter(Boolean);
  const online = members.filter(m => m.online).length;

  const onDrop = (e) => {
    e.preventDefault(); setOver(false);
    const cid = e.dataTransfer.getData("text/contact-id");
    if (cid) onDropContact(cid);
  };

  return (
    <article
      data-testid={IDS.talkgroups.card(tg.id)}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      className={`rounded-xl border bg-white p-4 flex flex-col gap-3 transition ${over ? "border-teal-500 ring-2 ring-teal-200 bg-teal-50/40" : "border-slate-200 border-dashed hover:border-slate-300"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200"><Users className="h-3 w-3" /> TALKGROUP</span>
            <span className="text-[10px] text-slate-400 font-mono">{online}/{members.length} online</span>
          </div>
          <h3 className="font-semibold text-sm tracking-tight truncate mt-1">{tg.name}</h3>
        </div>
        <button data-testid={IDS.talkgroups.delete(tg.id)} onClick={onDelete} className="text-slate-400 hover:text-red-600 transition" aria-label="Tear down talkgroup" title="Tear down talkgroup"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1.5 min-w-0">
          <div className="flex flex-wrap gap-1">
            {members.map(m => (
              <span key={m.id} data-testid={IDS.talkgroups.member(tg.id, m.id)} className="group/m relative inline-flex items-center gap-1 pl-0.5 pr-1.5 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-[10px]">
                <Avatar className="h-4 w-4"><AvatarImage src={m.avatar_url} alt="" /><AvatarFallback className="text-[8px]">{m.name[0]}</AvatarFallback></Avatar>
                <span className={`truncate max-w-[70px] ${m.online ? "" : "text-slate-400"}`}>{m.name.split(" ")[0]}</span>
                <button onClick={() => onRemoveMember(m.id)} className="ml-0.5 hidden group-hover/m:inline-flex text-slate-400 hover:text-red-600" aria-label={`Remove ${m.name}`}><X className="h-3 w-3" /></button>
              </span>
            ))}
            {members.length === 0 && <span className="text-[11px] text-slate-400">Drop units here</span>}
          </div>
          <div className="flex items-center gap-2">
            <span className="overline">{status === "transmitting" ? "TRANSMITTING" : "STANDBY"}</span>
            <Waveform active={status !== "idle"} color={status === "transmitting" ? "text-emerald-600" : "text-slate-300"} />
          </div>
        </div>
        <PTTButton testId={IDS.talkgroups.ptt(tg.id)} label={`Push to talk to talkgroup ${tg.name}`} color="teal" status={status} size="sm" disabled={online === 0} onPressStart={onPTTStart} onPressEnd={onPTTEnd} />
      </div>
    </article>
  );
}
